import {getEnv} from '@github-ui/client-env'
import {CopilotAuthTokenProvider} from '@github-ui/copilot-auth-token'
import {makeCAPIRequest} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {CommitsPayload} from '../types/commits-types'

type AuthToken = ReturnType<CopilotAuthTokenProvider['getAuthToken']> extends Promise<infer T> ? T : never

export const COPILOT_COMMIT_RECAP_ERROR_MESSAGE = 'Failed to generate commit recap'
const CHARS_PER_TOKEN = 3
const MAX_TOKENS_PER_REQUEST = 16000

function createPromptContent(
  owner: string,
  repo: string,
  branch: string,
  blobPath: string,
  commitGroups: CommitGroup[],
): string {
  const pathContext = blobPath ? ` at path ${blobPath}` : ''

  return `Below is a JSON array of metadata for a series of commits in the repository ${owner}/${repo}${pathContext} on branch ${branch}.

${JSON.stringify(commitGroups, null, 2)}

Each entry in the array represents metadata for a group of commits. These groups represent logical units of work, such as those merged together from a pull request or by the same author in a short timeframe.

You will provide an analysis of each commit group, writing a brief summary and scoring its size in terms of the file.

#### oid

This is the oid value of the first commit in the commit group, used as an identifier to ensure proper ordering.

#### summary

- These are terse, succinct paragraphs of 1-2 sentences, summarizing the work represented by the commit group.
- Focus on the content of the included commit diffEntries as a source of truth.
- Prefer a past-tense imperative "changelog entry" style, in simple, even "telegraphic" terms. Start the first sentence with a verb.
- Avoid directly referencing the author or the pull request that contributed the change.
- Avoid using the file as an object in the sentence, it's self-evident that the changes are being made to the file.
- Consider both the specific structure of the changes (functions/templates/config values/etc) and the general impact on the nature and purpose of the file.
- Descriptive context like the explicit purpose/intent of a change (from the commit message, pull request title, or pull request body) of the change may be used.
- If that context includes links (such as to a GitHub issue) that are NOT the author or pull request, you may include them in markdown format.
- Obvious or implicit intent ("simplify the codebase", "improve maintainability", "resolve syntax error") should not be mentioned.
- Good examples of summaries:
  - "Extracted most of the module to another file as part of Project Tailspin, moving the encoding helpers and deleting some unused functions."
  - "Updated config values for the \`GreatApp\` integration to increase max instances and resolve [incident #49](https://github.com/foo/bar/issues/123).
  - "Removed unused function \`functionName\`."
  - "Fixed typo in a variable name."
- Bad examples of summaries:
  - "Deleted functions \`func1\`, \`func2\`, \`func3\` and \`func4\` from the file, improving the code for readability".
  - "This commit by @monalisa in [pull request #49](https://github.com/foo/bar/pulls/49) adds a new feature to the application, improving the user experience."

#### size
- This is a "t-shirt size" subjectively scoring both the scope and impact of the changes in the commit group.
- Sizes include XS, S, M, L, XL.
- This should be judged entirely from the commit diffEntries and the nature and purpose of the file.
- The cognitive complexity of the changes is more important than the number of lines changed. "Added" is simpler than "updated".
- Examples of XS changes:
  - cleanly deleting a large number of unused functions
  - renaming a variable throughout a file via find-and-replace
  - a simple typo fix
- Examples of S changes:
  - created the file, which is simple and contains a couple helper functions imported elsewhere
  - flipping a couple configuration values
  - adding a new function
  - removing a section of markup to get rid of some HTML content
- Examples of M changes:
  - adding couple new functions that supply a new data field in an existing function
  - updating how one complex function works without changing its interface
- Example of L changes:
  - broke one complex function into several simpler ones, but also changed the initial arguments provided from elsewhere
  - created the file, which contains several API handlers with callbacks, imported dependecies, and dynamic templating
- Example of XL changes:
  - removed one of the two sections of configuraiton encapsulated by this file
  - complete overhaul of a class, fundamentally changing its interface and internal logic

Return your analysis as a JSON array of objects each containing these three fields, corresponding to the order of the provided commit groups:

[
  {
    "oid": "commit_oid_1",
    "summary": "Brief summary of the changes",
    "size": "S"
  },
  {
    "oid": "commit_oid_2",
    "summary": "Brief summary of the changes",
    "size": "M"
  }
]
`
}

function countTokens(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN)
}

// Helper functions for analyzing response matching
function countLinkedAuthors(response: string, authorsInRequest: Set<string>): number {
  let count = 0
  for (const author of Array.from(authorsInRequest)) {
    if (response.includes(`@${author}`) || response.includes(author)) {
      count++
    }
  }
  return count
}

function countLinkedPullRequests(response: string, pullRequestsInRequest: Set<number>): number {
  let count = 0
  for (const prNumber of Array.from(pullRequestsInRequest)) {
    if (response.includes(`#${prNumber}`) || response.includes(`[#${prNumber}]`)) {
      count++
    }
  }
  return count
}

function collectBatchTelemetryData(batch: CommitGroup[]) {
  const groupTypes = batch.map(group => group.type)
  const groupTypeCounts = groupTypes.reduce(
    (acc, type) => {
      acc[type] = (acc[type] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  const authorsInBatch = new Set<string>()
  const pullRequestsInBatch = new Set<number>()

  for (const group of batch) {
    for (const commit of group.commits) {
      if (commit.author?.login) {
        authorsInBatch.add(commit.author.login)
      }
    }
    if (group.pr?.number) {
      pullRequestsInBatch.add(group.pr.number)
    }
  }

  return {
    groupTypeCounts,
    authorsInBatch,
    pullRequestsInBatch,
    hasDiffData: batch.some(group => group.diffEntries && group.diffEntries.length > 0),
  }
}

function sendCommitRecapEvent(
  eventType: string,
  context: {
    refName: string
    blobPath?: string
    [key: string]: unknown
  },
) {
  const {refName, blobPath, ...otherContext} = context
  sendEvent(`copilot.commit-recap.${eventType}`, {
    refName,
    blobPath: blobPath || null,
    ...otherContext,
  })
}

function getBasePromptTokens(repoOwner: string, repoName: string, path: string, ref: string): number {
  // Create a minimal prompt content with empty commit groups to estimate base token count
  const basePrompt = createPromptContent(repoOwner, repoName, ref, path, [])
  return countTokens(basePrompt)
}

interface UseCopilotCommitRecapProps {
  repoOwner: string
  repoName: string
  refName?: string
  defaultBranch?: string
  filters: CommitsPayload['filters']
}

interface CommitGroup {
  type: string
  commits: Array<{
    oid: string
    message: string
    author?: {
      login?: string
      avatar_url?: string
      name: string
      email: string
      date: string
    }
  }>
  pr?: {number: number; title: string; body?: string}
  diffEntries?: DiffEntry[]
}

interface DiffEntry {
  old_path?: string
  new_path?: string
  additions: number
  deletions: number
  status: string
  text?: string
}

async function fetchCommitRollup(
  repoOwner: string,
  repoName: string,
  ref: string,
  filters: CommitsPayload['filters'],
): Promise<CommitGroup[]> {
  const path = filters.currentBlobPath || ''
  const params = new URLSearchParams()
  params.append('name', ref)
  if (filters.since) params.append('since', filters.since)
  if (filters.until) params.append('until', filters.until)
  if (filters.author) params.append('author', filters.author.login)

  const url = path
    ? `/${repoOwner}/${repoName}/commit_recap/${encodeURIComponent(path)}?${params}`
    : `/${repoOwner}/${repoName}/commit_recap.json?${params}`

  try {
    const response = await verifiedFetchJSON(url, {
      headers: {Accept: 'application/json'},
    })

    if (!response.ok) {
      sendCommitRecapEvent('failed', {
        refName: ref,
        blobPath: path,
        reason: 'commit_groups_fetch_failed',
        statusCode: response.status,
      })
      throw new Error(`Failed to fetch commit groups: ${response.status}`)
    }

    const commitGroups: CommitGroup[] = await response.json()

    // Send telemetry about commit groups returned
    const groupTypes = commitGroups.map(group => group.type)
    const groupTypeCounts = groupTypes.reduce(
      (acc, type) => {
        acc[type] = (acc[type] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

    sendCommitRecapEvent('commit-groups-returned', {
      refName: ref,
      blobPath: path,
      totalGroups: commitGroups.length,
      groupTypeCounts,
      hasAuthorFilter: !!filters.author,
      hasDateFilters: !!(filters.since || filters.until),
    })

    return commitGroups
  } catch (error) {
    sendCommitRecapEvent('failed', {
      refName: ref,
      blobPath: path,
      reason: 'commit_groups_fetch_error',
      errorMessage: error instanceof Error ? error.message : String(error),
    })
    throw error
  }
}

async function fetchPathCheck(
  repoOwner: string,
  repoName: string,
  ref: string,
  path: string,
): Promise<{is_file: boolean; show_popover: boolean}> {
  const params = new URLSearchParams()
  params.append('name', ref)

  const url = path
    ? `/${repoOwner}/${repoName}/commit_recap_check/${encodeURIComponent(path)}?${params}`
    : `/${repoOwner}/${repoName}/commit_recap_check.json?${params}`

  try {
    const response = await verifiedFetchJSON(url, {
      headers: {Accept: 'application/json'},
    })

    if (!response.ok) {
      throw new Error(`Failed to check path: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    throw error
  }
}

async function fetchSingleCommitGroupDiffs(
  repoOwner: string,
  repoName: string,
  commitGroup: CommitGroup,
  filters: CommitsPayload['filters'],
): Promise<CommitGroup> {
  try {
    const path = filters.currentBlobPath || ''
    const commitOids =
      commitGroup.type === 'pull_request' && commitGroup.commits.length > 0
        ? [commitGroup.commits[0]?.oid].filter(Boolean)
        : commitGroup.commits.map(commit => commit.oid)

    const params = new URLSearchParams()
    params.append('commit_oids', commitOids.join(','))

    const url = path
      ? `/${repoOwner}/${repoName}/commit_recap_diffs/${encodeURIComponent(path)}?${params}`
      : `/${repoOwner}/${repoName}/commit_recap_diffs.json?${params}`

    const response = await verifiedFetchJSON(url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    if (response.ok) {
      const diffData: {diff_entries: DiffEntry[]} = await response.json()
      return {...commitGroup, diffEntries: diffData.diff_entries || []}
    }

    return {...commitGroup, diffEntries: []}
  } catch {
    return {...commitGroup, diffEntries: []}
  }
}

export function useCopilotCommitRecap({
  repoOwner,
  repoName,
  refName,
  defaultBranch,
  filters,
}: UseCopilotCommitRecapProps) {
  const [analysisData, setAnalysisData] = useState<Array<{oid: string; summary: string; size: string}>>([])
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showRecapPanel, setShowRecapPanel] = useState(false)
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false)
  const isProcessingAnalysisRef = useRef(false)
  const previousFiltersRef = useRef<CommitsPayload['filters'] | null>(null)

  const copilotCommitHistoryIsEnabled = isFeatureEnabled('copilot_commit_recap')

  const ref = refName || defaultBranch || 'main'

  const {
    data: commitRollupData,
    isSuccess: isCommitRollupSuccess,
    isFetching: isCommitRollupFetching,
    refetch: refetchCommitRollup,
  } = useQuery({
    queryKey: ['commit-rollup', repoOwner, repoName, ref, filters],
    queryFn: () => fetchCommitRollup(repoOwner, repoName, ref, filters),
    enabled: false, // Manual trigger only
  })

  const {
    data: pathCheckData,
    isSuccess: _isPathCheckSuccess,
    refetch: refetchPathCheck,
  } = useQuery({
    queryKey: ['path-check', repoOwner, repoName, ref, filters.currentBlobPath],
    queryFn: () => fetchPathCheck(repoOwner, repoName, ref, filters.currentBlobPath || ''),
    enabled: copilotCommitHistoryIsEnabled && !!filters.currentBlobPath, // Auto-check when we have a path AND feature flag is enabled
  })

  const commitGroups = useMemo(() => commitRollupData || [], [commitRollupData])

  // This is a stopgap way of checking if the path is a file. If we ship this widely we will
  // want to know that up front, passing it through on the load of the commits page if possible.
  // Sidestepping moving cheese in high-traffic code for now.
  const showRecapButton = useMemo(() => {
    if (!filters.currentBlobPath) return false // No path = hide button (repo-wide or branch-wide)

    if (pathCheckData === undefined) return true // Default to showing otherwise, prioritize no flicker in most common use case

    return pathCheckData?.is_file !== false // Hide button only if we confirmed it's not a file
  }, [pathCheckData, filters.currentBlobPath])

  const showRecapPopover = useMemo(() => {
    return showRecapButton && pathCheckData?.show_popover === true
  }, [showRecapButton, pathCheckData])

  const processBatchedCopilotAnalysis = useCallback(
    async (allCommitGroups: CommitGroup[]) => {
      if (allCommitGroups.length === 0 || isProcessingAnalysisRef.current) return

      isProcessingAnalysisRef.current = true
      setIsAnalysisLoading(true)
      setAnalysisData([])
      setErrorMessage('')

      try {
        const path = filters.currentBlobPath || ''
        const authTokenProvider = new CopilotAuthTokenProvider([])
        const authToken = await authTokenProvider.getAuthToken()

        // Calculate base prompt tokens
        const baseTokens = getBasePromptTokens(repoOwner, repoName, path, ref)
        let currentBatch: CommitGroup[] = []
        let currentBatchTokens = baseTokens

        for (let i = 0; i < allCommitGroups.length; i++) {
          const commitGroup = allCommitGroups[i]
          if (!commitGroup) continue
          const enrichedGroup = await fetchSingleCommitGroupDiffs(repoOwner, repoName, commitGroup, filters)
          const groupTokens = countTokens(JSON.stringify(enrichedGroup, null, 2))

          // Check if adding this group would exceed our token limit
          if (currentBatchTokens + groupTokens > MAX_TOKENS_PER_REQUEST && currentBatch.length > 0) {
            // Process current batch before adding this group
            await sendBatchToCopilot(currentBatch, authToken, path, repoOwner, repoName, ref, setAnalysisData)

            // Start new batch with current group
            currentBatch = [enrichedGroup]
            currentBatchTokens = baseTokens + groupTokens
          } else {
            // Add to current batch
            currentBatch.push(enrichedGroup)
            currentBatchTokens += groupTokens
          }
        }

        // Process the final batch if it has any groups
        if (currentBatch.length > 0) {
          await sendBatchToCopilot(currentBatch, authToken, path, repoOwner, repoName, ref, setAnalysisData)
        }
      } catch {
        setErrorMessage(COPILOT_COMMIT_RECAP_ERROR_MESSAGE)
      } finally {
        setIsAnalysisLoading(false)
        isProcessingAnalysisRef.current = false
      }
    },
    [repoOwner, repoName, ref, filters],
  )

  // Trigger analysis when commit rollup is successful and panel is shown
  useEffect(() => {
    if (isCommitRollupSuccess && showRecapPanel && commitGroups.length > 0 && !isProcessingAnalysisRef.current) {
      processBatchedCopilotAnalysis(commitGroups)
    }
  }, [isCommitRollupSuccess, showRecapPanel, commitGroups, processBatchedCopilotAnalysis])

  const handleCopilotRecap = useCallback(async () => {
    sendCommitRecapEvent('requested', {
      refName: ref,
      blobPath: filters.currentBlobPath || '',
      hasAuthorFilter: !!filters.author,
      hasDateFilters: !!(filters.since || filters.until),
    })

    setAnalysisData([])
    setErrorMessage('')
    // Don't set loading state yet - wait until we confirm it's a file (for file-specific paths)

    // If no path, this is repo-wide/branch-wide - allow it to proceed
    if (!filters.currentBlobPath) {
      setShowRecapPanel(true)

      // For repo-wide, trigger commit rollup fetch if we don't have data
      if (!isCommitRollupSuccess || commitGroups.length === 0) {
        refetchCommitRollup()
      }
      return
    }

    // If we already have path check data and it's valid, proceed
    if (pathCheckData?.is_file === true) {
      setShowRecapPanel(true)

      // Trigger commit rollup fetch if we don't have data
      if (!isCommitRollupSuccess || commitGroups.length === 0) {
        refetchCommitRollup()
      }
    } else {
      try {
        const result = await refetchPathCheck()
        if (result.data?.is_file === true) {
          setShowRecapPanel(true)

          // Trigger commit rollup fetch if we don't have data
          if (!isCommitRollupSuccess || commitGroups.length === 0) {
            refetchCommitRollup()
          }
        } else {
          // Not a file
          setShowRecapPanel(false)
        }
      } catch {
        setShowRecapPanel(false)
      }
    }
  }, [refetchCommitRollup, refetchPathCheck, ref, filters, isCommitRollupSuccess, commitGroups, pathCheckData])

  const handleCloseRecap = useCallback(() => {
    setShowRecapPanel(false)
    setAnalysisData([])
    setErrorMessage('')
    setIsAnalysisLoading(false)
    isProcessingAnalysisRef.current = false
  }, [])

  // Close the recap panel when filters change (pagination, author, date filters, etc.)
  useEffect(() => {
    const currentFilters = filters
    const previousFilters = previousFiltersRef.current

    if (previousFilters === null) {
      previousFiltersRef.current = currentFilters
      return
    }

    const hasFiltersChanged =
      previousFilters.pagination?.endCursor !== currentFilters.pagination?.endCursor ||
      previousFilters.pagination?.startCursor !== currentFilters.pagination?.startCursor ||
      previousFilters.since !== currentFilters.since ||
      previousFilters.until !== currentFilters.until ||
      previousFilters.author?.login !== currentFilters.author?.login ||
      previousFilters.currentBlobPath !== currentFilters.currentBlobPath

    if (hasFiltersChanged && showRecapPanel) {
      setShowRecapPanel(false)
      setAnalysisData([])
      setErrorMessage('')
      setIsAnalysisLoading(false)
      isProcessingAnalysisRef.current = false
    }

    previousFiltersRef.current = currentFilters
  }, [filters, showRecapPanel])

  if (!copilotCommitHistoryIsEnabled) {
    return {
      response: {
        analysisData: [],
        errorMessage: '',
      },
      analysisData: [],
      errorMessage: '',
      isLoadingCopilotRecap: false,
      commitGroups: [],
      showRecapPanel: false,
      showRecapButton: false,
      showRecapPopover: false,
      handleCopilotRecap: () => {},
      handleCloseRecap: () => {},
    }
  }

  return {
    response: {
      analysisData,
      errorMessage,
    },
    analysisData,
    errorMessage,
    isLoadingCopilotRecap: isCommitRollupFetching || isAnalysisLoading,
    triggerAnalysis: processBatchedCopilotAnalysis,
    commitGroups,
    showRecapPanel,
    showRecapButton,
    showRecapPopover,
    handleCopilotRecap,
    handleCloseRecap,
  }
}

async function sendBatchToCopilot(
  batch: CommitGroup[],
  authToken: AuthToken,
  blobPath: string,
  owner: string,
  repo: string,
  branch: string,
  setAnalysisData: React.Dispatch<React.SetStateAction<Array<{oid: string; summary: string; size: string}>>>,
) {
  const content = createPromptContent(owner, repo, branch, blobPath, batch)
  const telemetryData = collectBatchTelemetryData(batch)

  // Send telemetry before CAPI request
  sendCommitRecapEvent('capi-request-sent', {
    refName: branch,
    blobPath,
    batchSize: batch.length,
    tokenCount: countTokens(content),
    groupTypeCounts: telemetryData.groupTypeCounts,
    uniqueAuthorsCount: telemetryData.authorsInBatch.size,
    uniquePullRequestsCount: telemetryData.pullRequestsInBatch.size,
    hasDiffData: telemetryData.hasDiffData,
  })

  try {
    const capiResponse = await makeCAPIRequest({
      basePath: getEnv().copilotApiOverrideUrl || 'https://api.githubcopilot.com',
      body: {
        messages: [
          {
            role: 'user',
            content,
          },
        ],
        model: 'gpt-4o-mini',
        stream: false,
        responseFormat: {
          type: 'json_schema',
          jsonSchema: {
            name: 'CommitGroupAnalysisList',
            description: 'Array of commit group analyses with summaries and size assessments',
            strict: true,
            schema: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  oid: {
                    type: 'string',
                    description:
                      'The oid value of the first commit in the commit group, used as an identifier to ensure proper ordering.',
                  },
                  summary: {
                    type: 'string',
                    description: 'A brief summary of the work represented by the commit group.',
                  },
                  size: {
                    type: 'string',
                    enum: ['XS', 'S', 'M', 'L', 'XL'],
                    description: 'A t-shirt size scoring both the scope and impact of the changes in the commit group.',
                  },
                },
                required: ['oid', 'summary', 'size'],
              },
            },
          },
        },
        temperature: 0.3,
      },
      path: '/chat/completions',
      method: 'POST',
      streamingResponse: false,
      authToken,
      integrationId: 'copilot-directory',
    })

    if (capiResponse.ok) {
      const responseJson = await capiResponse.json()
      const analysisResult = responseJson.choices?.[0]?.message?.content

      if (!analysisResult) {
        throw new Error('No analysis content received from Copilot API')
      }

      let parsedAnalyses: Array<{oid: string; summary: string; size: string}>

      // With structured outputs, the content should be already parsed JSON
      if (typeof analysisResult === 'object') {
        parsedAnalyses = analysisResult as Array<{oid: string; summary: string; size: string}>
      } else {
        // Handle string responses (including markdown-wrapped JSON)
        try {
          let jsonString = analysisResult as string

          // Check if response is wrapped in markdown code blocks
          if (jsonString.startsWith('```json\n') && jsonString.endsWith('\n```')) {
            jsonString = jsonString.slice(8, -4) // Remove ```json\n and \n```
          } else if (jsonString.startsWith('```\n') && jsonString.endsWith('\n```')) {
            jsonString = jsonString.slice(4, -4) // Remove ```\n and \n```
          }

          parsedAnalyses = JSON.parse(jsonString)
        } catch {
          throw new Error('Invalid analysis response format')
        }
      }

      // Create a map to preserve original order using oid
      const commitGroupOrderMap = new Map(
        batch
          .filter(group => group.commits.length > 0) // Ensure commits array is non-empty
          .map((group, index) => [group.commits[0]?.oid, index]),
      )

      // Sort analyses by original commit group order using oid
      const sortedAnalyses = parsedAnalyses?.sort((a, b) => {
        const orderA = commitGroupOrderMap.get(a.oid) ?? Number.MAX_SAFE_INTEGER
        const orderB = commitGroupOrderMap.get(b.oid) ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })

      // Use summary as analysis content and log size
      const analysisText = sortedAnalyses
        .map(analysis => {
          /* eslint-disable-next-line no-console */
          console.log(`Commit group ${analysis.oid}: Size ${analysis.size}`)
          return analysis.summary
        })
        .join('\n\n')

      // Append new analyses to existing data
      setAnalysisData(prev => [...prev, ...sortedAnalyses])

      // Send success telemetry with response analysis
      sendCommitRecapEvent('capi-response-received', {
        refName: branch,
        blobPath,
        batchSize: batch.length,
        responseLength: analysisText.length,
        linkedAuthorsPercentage:
          telemetryData.authorsInBatch.size > 0
            ? countLinkedAuthors(analysisText, telemetryData.authorsInBatch) / telemetryData.authorsInBatch.size
            : 0,
        linkedPullRequestsPercentage:
          telemetryData.pullRequestsInBatch.size > 0
            ? countLinkedPullRequests(analysisText, telemetryData.pullRequestsInBatch) /
              telemetryData.pullRequestsInBatch.size
            : 0,
      })
    } else {
      sendCommitRecapEvent('failed', {
        refName: branch,
        blobPath,
        reason: 'capi_response_not_ok',
        statusCode: capiResponse.status,
      })
      throw new Error(COPILOT_COMMIT_RECAP_ERROR_MESSAGE)
    }
  } catch (error) {
    sendCommitRecapEvent('failed', {
      refName: branch,
      blobPath,
      reason: 'capi_request_failed',
      errorMessage: error instanceof Error ? error.message : String(error),
    })
    throw error
  }
}
